/**
 * Challenge codes (MP-28) and the Hall of Records (MP-27), parsed from the bundle's `modes.challenge` and
 * `modes.records` (balance/src/modes.json; docs/MODES.md §7–§8). The rules are in meta/challengeCode.ts and
 * meta/records.ts.
 */

export interface ChallengeData {
  /** A code's run uses the `normalised` profile (MP-32), VIP on top when `normalised.allowVip`. */
  readonly normalised: boolean;
  readonly unlockAfterLevel: number;
  /** The lowest campaign layout a code may borrow. */
  readonly levelFrom: number;
  /** The raiders' power anchor, as Daily Road's: health × levelBase(powerLevel) / levelBase(level). */
  readonly powerLevel: number;
  /** The run deals the pool minus the banned towers: at least this many. */
  readonly minTowers: number;
  /** Weekly Trial modifiers a code may hold (the code has two slots). */
  readonly maxModifiers: number;
  /** The Fortress HP caps a maker can pick (0 = none), in the picker's order. */
  readonly livesCaps: readonly number[];
  /** Codes the Hall of Records keeps with their bests, newest first. */
  readonly recentKept: number;
}

export interface RecordsData {
  /** Daily Road days the Hall shows (read from the `dailyRoad` section, which keeps at least these). */
  readonly dailyDays: number;
  /** Event keys remembered so a run is counted once. */
  readonly keysKept: number;
  /** Race Week weeks kept. */
  readonly raceWeeksKept: number;
}

/** No data (an old bundle, a test fixture): challenge codes never open. */
export function emptyChallenge(): ChallengeData {
  return { normalised: true, unlockAfterLevel: Number.MAX_SAFE_INTEGER, levelFrom: 1, powerLevel: 1, minTowers: 1, maxModifiers: 0, livesCaps: [0], recentKept: 1 };
}
export function emptyRecords(): RecordsData { return { dailyDays: 1, keysKept: 1, raceWeeksKept: 1 }; }

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Json = any;

function int(o: Json, key: string, where: string, min = 0): number {
  const v = o !== null && typeof o === "object" ? o[key] : undefined;
  if (typeof v === "number" && Number.isInteger(v) && v >= min) return v;
  throw new TypeError(`modes.${where}.${key}: expected an integer >= ${min}, got ${JSON.stringify(v)}`);
}

export function parseChallenge(c: Json): ChallengeData {
  if (c === undefined) return emptyChallenge();
  const caps = c.livesCaps;
  if (!Array.isArray(caps) || caps.length === 0 || !caps.every((n) => Number.isInteger(n) && n >= 0)) throw new TypeError("modes.challenge.livesCaps: integers >= 0");
  return {
    normalised: c.normalised === true, unlockAfterLevel: int(c, "unlockAfterLevel", "challenge", 1),
    levelFrom: int(c, "levelFrom", "challenge", 1), powerLevel: int(c, "powerLevel", "challenge", 1), minTowers: int(c, "minTowers", "challenge", 1),
    maxModifiers: int(c, "maxModifiers", "challenge"), livesCaps: caps as number[], recentKept: int(c, "recentKept", "challenge", 1),
  };
}

export function parseRecords(r: Json): RecordsData {
  if (r === undefined) return emptyRecords();
  return { dailyDays: int(r, "dailyDays", "records", 1), keysKept: int(r, "keysKept", "records", 1), raceWeeksKept: int(r, "raceWeeksKept", "records", 1) };
}
